import { getAmazonUrl, type AmazonProduct } from "@/data/amazonProducts";
import { trackAmazonAffiliateClick, trackOutboundClick } from "@/lib/analytics";

interface AmazonProductBoxProps {
  products: AmazonProduct[];
  postSlug: string;
}

export default function AmazonProductBox({ products, postSlug }: AmazonProductBoxProps) {
  if (!products || products.length === 0) return null;

  const handleClick = (product: AmazonProduct) => {
    trackOutboundClick({
      link_url: getAmazonUrl(product.asin),
      link_text: product.title,
      link_location: "recommended_resources",
    });
    trackAmazonAffiliateClick({
      product_title: product.title,
      product_asin: product.asin,
      product_category: product.category,
      post_slug: postSlug,
      link_location: "recommended_resources",
    });
  };

  return (
    <section className="mt-16 pt-10 border-t border-white/10" aria-labelledby="recommended-resources">
      <div className="flex items-center gap-3 mb-2">
        <span className="h-px w-8 bg-amber-400/80" aria-hidden="true" />
        <span className="text-mono text-[10px] text-amber-300/85">Further reading</span>
      </div>
      <h2 id="recommended-resources" className="font-serif text-2xl text-foreground mb-2">
        Resources for this workflow
      </h2>
      <p className="max-w-2xl text-sm text-muted-foreground mb-6">
        Affiliate links. As an Amazon Associate I earn from qualifying purchases. Product price and availability are shown on Amazon UK.
      </p>
      <div className="grid border border-white/10 bg-white/[0.02] sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => (
          <a
            key={product.asin}
            href={getAmazonUrl(product.asin)}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={() => handleClick(product)}
            className="group flex min-h-[220px] flex-col border-b border-white/10 p-5 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0 lg:min-h-[236px] hover:bg-white/[0.035] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-400/70 transition-colors duration-200"
          >
            <div className="flex items-start gap-3 mb-4">
              {product.imageUrl && (
                <img
                  src={product.imageUrl}
                  alt={product.title}
                  className="w-16 h-16 object-contain border border-white/10 bg-[#0a111b] p-1.5 flex-shrink-0"
                  loading="lazy"
                  decoding="async"
                  width={64}
                  height={64}
                />
              )}
              <div className="min-w-0">
                <span className="block text-[10px] uppercase tracking-[0.12em] text-amber-300/80 mb-1">Recommended resource</span>
                <h3 className="font-serif text-base text-foreground leading-tight group-hover:text-amber-300 transition-colors line-clamp-2">
                  {product.title}
                </h3>
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-4 flex-1">
              {product.description}
            </p>
            <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/10">
              <span className="text-xs text-amber-300/80 group-hover:text-amber-200 transition-colors">
                View on Amazon UK →
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
